import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCustom, useTranslate } from "@refinedev/core";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { FilterInklapKnop, useFilterInklap } from "../components/FilterInklap";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import {
  DateField,
  PawIcon,
  ClockIcon,
  HomeIcon,
  MicrochipIcon,
  CheckIcon,
  DropletIcon,
  AlertTriangleIcon,
  ArchiveIcon,
  PencilIcon,
  GearIcon,
  TagIcon,
  UserIcon,
  PaletteIcon,
  CombIcon,
  CalendarIcon,
  VenusIcon,
  PinIcon,
  IdCardIcon,
  FileTextIcon,
  ToggleField,
} from "@fawkes/ui";

// ── wire types (GET /api/medicijn-overzicht) ───────────────────────────────────────────────────────
// Faithful port of the Jmix MedicijnOverzichtService.bouwOverzicht payload (see api
// MedicijnOverzichtResponse). One card per present animal that has at least one medication prescription;
// the medication fields are the leading ("driver") prescription, the status is one of four buckets.
export interface MedicijnCard {
  dierId: number;
  verblijfNummer: string | null;
  naam: string | null;
  ras: string | null;
  diersoort: string | null;
  leeftijd: string | null;
  geboortedatum: string | null;
  geslacht: string | null;
  gecastreerd: string | null;
  castratiedatum: string | null;
  kleur: string | null;
  vacht: string | null;
  paspoortnummer: string | null;
  bijzonderheden: string | null;
  herkomst: string | null;
  opnamedatum: string | null;
  kennel: string | null;
  chipnummer: string | null;
  fotoUrl: string | null;
  status: string;
  medicijn: string | null;
  aantalMedicijnen: number;
  categorie: string | null;
  dosering: string | null;
  frequentie: string | null;
  toedieningsvorm: string | null;
  voorgeschrevenDoor: string | null;
  startDatum: string | null;
  stopDatum: string | null;
  laatsteToediening: string | null;
  volgendeToediening: string | null;
  volgendeToedieningIso: string | null;
  volgendeDagen: number | null;
  pleeggezin: boolean;
}
interface MedicijnResponse {
  kpi: { vandaag: number; binnenkort: number; actief: number; historisch: number; totaal: number };
  cards: MedicijnCard[];
  filters: { diersoorten: string[]; categorieen: string[] };
}

type Status = "TOEDIENING_VANDAAG" | "BINNENKORT_STOPPEN" | "ACTIEF" | "HISTORISCH";
const ALL_STATUSES: Status[] = ["TOEDIENING_VANDAAG", "BINNENKORT_STOPPEN", "ACTIEF", "HISTORISCH"];
// The source opens with HISTORISCH hidden (only running medication); "Filters wissen" returns here too.
const DEFAULT_STATUSES: Status[] = ["TOEDIENING_VANDAAG", "BINNENKORT_STOPPEN", "ACTIEF"];
// Jmix urgency ordering: most-urgent first.
const URGENCY: Record<string, number> = {
  TOEDIENING_VANDAAG: 0,
  BINNENKORT_STOPPEN: 1,
  ACTIEF: 2,
  HISTORISCH: 3,
};
// Maps each status to a tone class (shared status-overview CSS): vandaag=danger/red, binnenkort=warning,
// actief=success/green, historisch=neutral/grey (Jmix used rood/oranje/groen/grijs).
const STATUS_TONE: Record<string, string> = {
  TOEDIENING_VANDAAG: "danger",
  BINNENKORT_STOPPEN: "warning",
  ACTIEF: "success",
  HISTORISCH: "neutral",
};
const PER_PAGE = 20;

// Sort options (Jmix SORTEER_OPTIES): most-urgent first, name, next-administration, species.
type MedSort =
  | "urgent" | "naam" | "naamDesc" | "volgende" | "diersoort" | "jongste" | "oudste" | "opnameNieuwste"
  | "opnameOudste" | "kennel";
const MED_SORTS: MedSort[] = [
  "urgent", "naam", "naamDesc", "volgende", "diersoort", "jongste", "oudste", "opnameNieuwste", "opnameOudste", "kennel",
];

/** "dd-MM-yyyy" (zoals de kaarten datums tonen) → "yyyy-MM-dd", zodat datums als tekst te vergelijken zijn. */
function isoVanDmy(dmy: string | null): string | null {
  const m = dmy ? /^(\d{2})-(\d{2})-(\d{4})/.exec(dmy.trim()) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/** Tekst/datum-vergelijking met lege waarden altijd achteraan, ongeacht de richting. */
function vergelijkLeegAchteraan(a: string | null, b: string | null, richting: 1 | -1): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, "nl", { numeric: true }) * richting;
}

type Weergave = "grid" | "compact" | "lijst";

export interface MedicijnOverzichtProps {
  /** Open the animal detail (the card click + the Toediening-registreren action both land here). */
  onOpenAnimal: (dierId: string) => void;
}

const FIELD_ICON: Record<string, ReactNode> = {
  medicijn: <DropletIcon width={15} height={15} />,
  categorie: <TagIcon width={15} height={15} />,
  dosering: <DropletIcon width={15} height={15} />,
  frequentie: <ClockIcon width={15} height={15} />,
  toedieningsvorm: <DropletIcon width={15} height={15} />,
  laatsteToediening: <CheckIcon width={15} height={15} />,
  volgendeToediening: <ClockIcon width={15} height={15} />,
  startDatum: <CalendarIcon width={15} height={15} />,
  stopDatum: <CalendarIcon width={15} height={15} />,
  voorgeschrevenDoor: <UserIcon width={15} height={15} />,
  diersoort: <PawIcon width={15} height={15} />,
  kleur: <PaletteIcon width={15} height={15} />,
  vacht: <CombIcon width={15} height={15} />,
  leeftijd: <CalendarIcon width={15} height={15} />,
  geboortedatum: <CalendarIcon width={15} height={15} />,
  geslacht: <VenusIcon width={15} height={15} />,
  gecastreerd: <CheckIcon width={15} height={15} />,
  castratiedatum: <CalendarIcon width={15} height={15} />,
  herkomst: <PinIcon width={15} height={15} />,
  opnamedatum: <CalendarIcon width={15} height={15} />,
  kennel: <HomeIcon width={15} height={15} />,
  paspoortnummer: <IdCardIcon width={15} height={15} />,
  bijzonderheden: <FileTextIcon width={15} height={15} />,
  chipnummer: <MicrochipIcon width={15} height={15} />,
};

// The selectable card-meta-rows — a faithful port of the source `FIELDS` (same 24 keys, same order).
// `pill` marks the status-coloured "Volgende toediening" row. Rows are rendered in this order, filtered
// by the field chooser; `STANDAARD_AAN` is the source default set (Volgende toediening sorts before
// Kennel, matching the source order).
const FIELDS: Array<{ key: string; get: (c: MedicijnCard) => string | null; pill?: boolean }> = [
  { key: "medicijn", get: (c) => medicijnTekst(c) },
  { key: "categorie", get: (c) => c.categorie },
  { key: "dosering", get: (c) => c.dosering },
  { key: "frequentie", get: (c) => c.frequentie },
  { key: "toedieningsvorm", get: (c) => c.toedieningsvorm },
  { key: "laatsteToediening", get: (c) => c.laatsteToediening },
  { key: "volgendeToediening", get: (c) => c.volgendeToediening, pill: true },
  { key: "startDatum", get: (c) => c.startDatum },
  { key: "stopDatum", get: (c) => c.stopDatum },
  { key: "voorgeschrevenDoor", get: (c) => c.voorgeschrevenDoor },
  { key: "diersoort", get: (c) => c.diersoort },
  { key: "kleur", get: (c) => c.kleur },
  { key: "vacht", get: (c) => c.vacht },
  { key: "leeftijd", get: (c) => c.leeftijd },
  { key: "geboortedatum", get: (c) => c.geboortedatum },
  { key: "geslacht", get: (c) => c.geslacht },
  { key: "gecastreerd", get: (c) => c.gecastreerd },
  { key: "castratiedatum", get: (c) => c.castratiedatum },
  { key: "herkomst", get: (c) => c.herkomst },
  { key: "opnamedatum", get: (c) => c.opnamedatum },
  { key: "kennel", get: (c) => c.kennel },
  { key: "paspoortnummer", get: (c) => c.paspoortnummer },
  { key: "bijzonderheden", get: (c) => c.bijzonderheden },
  { key: "chipnummer", get: (c) => c.chipnummer },
];
const STANDAARD_AAN = new Set([
  "medicijn",
  "dosering",
  "frequentie",
  "laatsteToediening",
  "volgendeToediening",
  "kennel",
]);
type VeldenState = Record<string, boolean>;
const VELDEN_DEFAULT: VeldenState = Object.fromEntries(FIELDS.map((f) => [f.key, STANDAARD_AAN.has(f.key)]));

/** Species illustration for the per-species stat cards (source `SoortKaarten`). */
function speciesIconSrc(naam: string | null): string {
  const n = (naam ?? "").toLowerCase();
  let file = "overig";
  if (n.includes("hond")) file = "hond";
  else if (n.includes("kat")) file = "kat";
  else if (n.includes("konijn")) file = "konijn";
  else if (n.includes("vogel")) file = "vogel";
  else if (n.includes("knaag")) file = "knaagdier";
  return `/species/${file}.png`;
}

/** Medication name + a "+N" badge when the animal has more than one running prescription. */
function medicijnTekst(c: MedicijnCard): string | null {
  if (!c.medicijn) return null;
  const extra = c.aantalMedicijnen > 1 ? ` +${c.aantalMedicijnen - 1}` : "";
  return `${c.medicijn}${extra}`;
}

/**
 * "Medicijn overzicht" route — the present-animal medication overview (Jmix `MedicijnOverzichtView`
 * migration). The medication counterpart of the Entingen / Ontwormen overviews: same card/KPI/filter
 * shell (shared status-overview CSS), with the four medication status buckets, the medication-specific
 * card fields and a category filter. Backed by GET /api/medicijn-overzicht.
 */
/**
 * Field chooser (source `VeldenChooser`): a "Velden" button opening a checkbox panel over all 24
 * selectable fields. Edits are staged in a draft, applied on "Opslaan"; "Annuleren"/outside-click
 * discards them. Mirrors the entingen/ontwormen overview control.
 */
function VeldenChooser({
  velden,
  onChange,
  fieldLabel,
  openLabel,
  titleLabel,
  cancelLabel,
  saveLabel,
}: {
  velden: VeldenState;
  onChange: (v: VeldenState) => void;
  fieldLabel: (key: string) => string;
  openLabel: string;
  titleLabel: string;
  cancelLabel: string;
  saveLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<VeldenState>(velden);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);
  const openPanel = () => {
    setDraft(velden);
    setOpen(true);
  };
  const save = () => {
    onChange(draft);
    setOpen(false);
  };
  return (
    <div className="status-veldkiezer" ref={ref}>
      <button
        type="button"
        className="status-veldkiezer__btn"
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <GearIcon width={15} height={15} /> {openLabel}
      </button>
      {open && (
        <div className="status-veldkiezer__panel">
          <div className="status-veldkiezer__title">{titleLabel}</div>
          <div className="status-veldkiezer__list">
            {FIELDS.map((f) => (
              <label key={f.key} className="status-veldkiezer__row">
                <input
                  type="checkbox"
                  checked={!!draft[f.key]}
                  onChange={() => setDraft({ ...draft, [f.key]: !draft[f.key] })}
                />
                <span className="status-veldkiezer__label">{fieldLabel(f.key)}</span>
              </label>
            ))}
          </div>
          <div className="status-veldkiezer__foot">
            <button type="button" className="status-veldkiezer__cancel" onClick={() => setOpen(false)}>
              {cancelLabel}
            </button>
            <button type="button" className="status-veldkiezer__save" onClick={save}>
              {saveLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Medicijnstatus multi-select (source `StatusFilter`): a "Geen/Alle/N geselecteerd" button opening a
 * checkbox panel with a colour dot per status. Mirrors the entingen/ontwormen overview control.
 */
function StatusMultiSelect({
  selected,
  onToggle,
  label,
  statusLabel,
}: {
  selected: Status[];
  onToggle: (s: Status) => void;
  label: (selected: Status[]) => string;
  statusLabel: (s: Status) => string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);
  return (
    <div className="status-msel" ref={ref}>
      <button type="button" className="status-msel__btn status-overview__select" onClick={() => setOpen(!open)}>
        <span>{label(selected)}</span>
        <span className="status-msel__caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="status-msel__panel" role="listbox">
          {ALL_STATUSES.map((s) => (
            <label key={s} className="status-msel__item">
              <input type="checkbox" checked={selected.includes(s)} onChange={() => onToggle(s)} />
              <span className={`status-msel__dot status-msel__dot--${STATUS_TONE[s] ?? "neutral"}`} />
              {statusLabel(s)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function MedicijnOverzicht({ onOpenAnimal }: MedicijnOverzichtProps) {
  const t = useTranslate();
  const k = (suffix: string) => t(`medicijnOverzicht.${suffix}`);

  // Op een telefoon staat het filterblok dicht; alleen het zoekveld blijft staan. Zie
  // FilterInklap. Op een breed scherm is `toon` altijd waar en `mobiel` nooit.
  const { mobiel, open: filtersOpen, toon, wissel } = useFilterInklap();

  // ── filter state ───────────────────────────────────────────────────────────────────────────────
  // diersoort + category are SERVER-side filters (the api takes them as query params); search + status
  // are client-side (status is driven by the clickable KPI tiles, like Jmix / the entingen overview).
  const [search, setSearch] = useState("");
  const [diersoort, setDiersoort] = useState("");
  const [categorie, setCategorie] = useState("");
  const [statuses, setStatuses] = useState<Status[]>([...DEFAULT_STATUSES]);
  // Jmix medicijn-overzicht parity: a next-administration date-range filter + a sort selector (both
  // client-side over the fetched cards). These are medicijn-specific (the entingen/ontwormen overviews
  // have no administration schedule), so they live only here.
  const [vanaf, setVanaf] = useState("");
  const [tot, setTot] = useState("");
  // Wat er aanstaat, voor het getal op de inklapknop. De statusselectie telt alleen mee als hij
  // van de standaard afwijkt — anders zou de knop altijd een filter melden dat niemand zette.
  const statusAfwijkend = (gekozen: Status[]) =>
    gekozen.length !== DEFAULT_STATUSES.length || gekozen.some((x) => !DEFAULT_STATUSES.includes(x));
  const [sortBy, setSortBy] = useState<MedSort>("urgent");
  const [velden, setVelden] = useState<VeldenState>(VELDEN_DEFAULT);
  const [weergave, setWeergave] = useState<Weergave>("grid");
  const [page, setPage] = useState(1);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (diersoort) p.set("diersoort", diersoort);
    if (categorie) p.set("categorie", categorie);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [diersoort, categorie]);

  const { data, isLoading, isError } = useCustom<MedicijnResponse>({
    url: `medicijn-overzicht${query}`,
    method: "get",
  });
  const result = data?.data;
  const kpi = result?.kpi ?? { vandaag: 0, binnenkort: 0, actief: 0, historisch: 0, totaal: 0 };
  const cards = result?.cards ?? [];
  const diersoorten = result?.filters?.diersoorten ?? [];
  const categorieen = result?.filters?.categorieen ?? [];

  // Per-species stat cards (source SoortKaarten) — counted over all cards, foster included.
  const soortTellingen = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cards) if (c.diersoort) m.set(c.diersoort, (m.get(c.diersoort) ?? 0) + 1);
    return [...m.entries()].map(([naam, aantal]) => ({ naam, aantal })).sort((a, b) => b.aantal - a.aantal);
  }, [cards]);
  // Source parity: Totaal counts ALL fetched animals (incl. speciesless), not just the species buckets.
  const totaalDieren = cards.length;

  // Foster hidden by default (source parity); the toggle only affects the list, not the KPIs/stats.
  const [includeFoster, setIncludeFoster] = useState(false);
  const fosterCount = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards
      .filter((c) => c.pleeggezin)
      .filter((c) => statuses.includes(c.status as Status))
      .filter((c) => {
        if (!q) return true;
        const hay = `${c.naam ?? ""} ${c.chipnummer ?? ""} ${c.verblijfNummer ?? ""} ${c.kennel ?? ""} ${c.medicijn ?? ""}`.toLowerCase();
        return hay.includes(q);
      }).length;
  }, [cards, statuses, search]);

  // Reset paging whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [diersoort, categorie, search, statuses, vanaf, tot, sortBy, includeFoster]);

  // ── client-side search + status + next-administration date filter + sort ─────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const nullLast = (v: number | null) => (v == null ? Number.POSITIVE_INFINITY : v);
    return cards
      .filter((c) => statuses.includes(c.status as Status))
      .filter((c) => includeFoster || !c.pleeggezin)
      .filter((c) => {
        if (!q) return true;
        const hay =
          `${c.naam ?? ""} ${c.chipnummer ?? ""} ${c.verblijfNummer ?? ""} ${c.kennel ?? ""} ${c.medicijn ?? ""}`.toLowerCase();
        return hay.includes(q);
      })
      .filter((c) => {
        // Next-administration date range (Jmix gefilterdBasis): a card with a date range set but no
        // upcoming administration is excluded.
        if (!vanaf && !tot) return true;
        if (!c.volgendeToedieningIso) return false;
        if (vanaf && c.volgendeToedieningIso < vanaf) return false;
        if (tot && c.volgendeToedieningIso > tot) return false;
        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "naam":
            return (a.naam ?? "").localeCompare(b.naam ?? "");
          case "diersoort":
            return (a.diersoort ?? "").localeCompare(b.diersoort ?? "");
          case "naamDesc":
            return (b.naam ?? "").localeCompare(a.naam ?? "");
          case "jongste":
            return vergelijkLeegAchteraan(isoVanDmy(a.geboortedatum), isoVanDmy(b.geboortedatum), -1);
          case "oudste":
            return vergelijkLeegAchteraan(isoVanDmy(a.geboortedatum), isoVanDmy(b.geboortedatum), 1);
          case "opnameNieuwste":
            return vergelijkLeegAchteraan(isoVanDmy(a.opnamedatum), isoVanDmy(b.opnamedatum), -1);
          case "opnameOudste":
            return vergelijkLeegAchteraan(isoVanDmy(a.opnamedatum), isoVanDmy(b.opnamedatum), 1);
          case "kennel":
            return vergelijkLeegAchteraan(a.kennel, b.kennel, 1);
          case "volgende":
            return nullLast(a.volgendeDagen) - nullLast(b.volgendeDagen);
          default: {
            const u = (URGENCY[a.status] ?? 9) - (URGENCY[b.status] ?? 9);
            if (u !== 0) return u;
            return nullLast(a.volgendeDagen) - nullLast(b.volgendeDagen);
          }
        }
      });
  }, [cards, statuses, search, vanaf, tot, sortBy, includeFoster]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PER_PAGE;
  const visible = filtered.slice(start, start + PER_PAGE);

  // Uploaded photo per visible animal (authenticated blob → object URL); see useAnimalPhotoUrls. Falls
  // back to the feed's online `fotoUrl`. Restores the real photo like acc (a plain <img src> can't carry
  // the bearer, and the Jmix /dier-foto path 404s in the SPA).
  const photoUrls = useAnimalPhotoUrls(visible.map((c) => c.dierId));
  const photoOf = (c: MedicijnCard): string | null => photoUrls[String(c.dierId)] ?? c.fotoUrl;

  // KPI click: a single-status focus toggles back to "all" (Jmix toggleKpi); Totaal selects all.
  const toggleKpi = (s: Status | "TOTAAL") => {
    if (s === "TOTAAL") {
      setStatuses([...ALL_STATUSES]);
      return;
    }
    setStatuses(statuses.length === 1 && statuses[0] === s ? [...ALL_STATUSES] : [s]);
  };
  const isOnly = (s: Status) => statuses.length === 1 && statuses[0] === s;
  const isAll = statuses.length === ALL_STATUSES.length;

  const clearFilters = () => {
    setSearch("");
    setDiersoort("");
    setCategorie("");
    setStatuses([...DEFAULT_STATUSES]);
    setVanaf("");
    setTot("");
    setIncludeFoster(false);
  };

  return (
    <div className="status-overview">
      {/* Titel links, soortkaarten rechts — één regel, zoals de bron (`.kop`). */}
      <div className="status-overview__kop">
        {/* Header: icon + title + subtitle */}
        <header className="status-overview__header">
          <span className="status-overview__icon" aria-hidden="true">
            <PillIcon width={28} height={28} />
          </span>
          <div>
            <h1 className="status-overview__title">{k("title")}</h1>
            <p className="status-overview__subtitle">{k("subtitle")}</p>
          </div>
        </header>

        {/* Per-species stat cards (source SoortKaarten) — clickable diersoort shortcut + Totaal. */}
        {soortTellingen.length > 0 && (
          <div className="da-overview__stats status-overview__stats">
            {soortTellingen.map((s) => (
              <button
                key={s.naam}
                type="button"
                className={`da-overview__stat${diersoort === s.naam ? " da-overview__stat--active" : ""}`}
                onClick={() => setDiersoort(diersoort === s.naam ? "" : s.naam)}
              >
                <span className="da-overview__stat-icon">
                  <img src={speciesIconSrc(s.naam)} alt="" />
                </span>
                <span className="da-overview__stat-body">
                  <span className="da-overview__stat-value">{s.aantal}</span>
                  <span className="da-overview__stat-label">{s.naam}</span>
                </span>
              </button>
            ))}
            <button
              type="button"
              className={`da-overview__stat${diersoort === "" ? " da-overview__stat--active" : ""}`}
              onClick={() => setDiersoort("")}
            >
              <span className="da-overview__stat-icon da-overview__stat-icon--total" aria-hidden="true">
                <PawIcon width={22} height={22} />
              </span>
              <span className="da-overview__stat-body">
                <span className="da-overview__stat-value">{totaalDieren}</span>
                <span className="da-overview__stat-label">{k("stat.total")}</span>
              </span>
            </button>
          </div>
        )}
      </div>

      {/* KPI tiles — clickable status filters (vandaag / binnenkort / actief / historisch / totaal). */}
      <section className="status-overview__kpis" aria-label={k("kpi.aria")}>
        <KpiTile tone="danger" value={kpi.vandaag} label={k("kpi.vandaag")} active={isOnly("TOEDIENING_VANDAAG")} onClick={() => toggleKpi("TOEDIENING_VANDAAG")} icon={<ClockIcon width={20} height={20} />} />
        <KpiTile tone="warning" value={kpi.binnenkort} label={k("kpi.binnenkort")} sub={k("kpi.binnenkortSub")} active={isOnly("BINNENKORT_STOPPEN")} onClick={() => toggleKpi("BINNENKORT_STOPPEN")} icon={<AlertTriangleIcon width={20} height={20} />} />
        <KpiTile tone="success" value={kpi.actief} label={k("kpi.actief")} active={isOnly("ACTIEF")} onClick={() => toggleKpi("ACTIEF")} icon={<PillIcon width={20} height={20} />} />
        <KpiTile tone="neutral" value={kpi.historisch} label={k("kpi.historisch")} active={isOnly("HISTORISCH")} onClick={() => toggleKpi("HISTORISCH")} icon={<ArchiveIcon width={20} height={20} />} />
        <KpiTile tone="total" value={kpi.totaal} label={k("kpi.totaal")} active={isAll} onClick={() => toggleKpi("TOTAAL")} icon={<PawIcon width={20} height={20} />} />
      </section>

      {/* Filters: search + diersoort + category (both server-side) + clear. */}
      <section className="status-overview__filters">
        <div className="fawkes-products-search status-overview__search">
          {/* Source `.zoekIcon`: the 🔍 emoji, not a line icon. */}
          <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={k("search.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={k("search.placeholder")}
          />
        </div>
        {mobiel && (
          <FilterInklapKnop
            open={filtersOpen}
            actief={
              (diersoort ? 1 : 0) +
              (categorie ? 1 : 0) +
              (vanaf ? 1 : 0) +
              (tot ? 1 : 0) +
              (statusAfwijkend(statuses) ? 1 : 0)
            }
            label={t("filter.inklap")}
            onClick={wissel}
          />
        )}

        {toon && (
        <>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.diersoort")}</span>
          <select className="status-overview__select" value={diersoort} onChange={(e) => setDiersoort(e.target.value)}>
            <option value="">{k("filter.all")}</option>
            {diersoorten.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <div className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.status")}</span>
          <StatusMultiSelect
            selected={statuses}
            onToggle={(s) =>
              setStatuses(statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s])
            }
            label={(sel) =>
              sel.length === 0
                ? k("filter.statusNone")
                : sel.length === ALL_STATUSES.length
                  ? k("filter.all")
                  : k("filter.statusN").replace("%d", String(sel.length))
            }
            statusLabel={(s) => k(`status.${s}`)}
          />
        </div>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.categorie")}</span>
          <select className="status-overview__select" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
            <option value="">{k("filter.all")}</option>
            {categorieen.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.vanaf")}</span>
          <DateField className="status-overview__select" value={vanaf} aria-label={k("filter.vanaf")} onChange={(e) => setVanaf(e.target.value)} />
        </label>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.tot")}</span>
          <DateField className="status-overview__select" value={tot} aria-label={k("filter.tot")} onChange={(e) => setTot(e.target.value)} />
        </label>
        <div className="status-overview__field status-overview__field--action">
          {/* Source `.wisBtn`: white pill + green-dark bold text + ↻ icon. */}
          <button type="button" className="da-overview__wis" onClick={clearFilters}>
            <span className="da-overview__wis-icon" aria-hidden="true">↻</span>
            {k("filter.clear")}
          </button>
        </div>
        {/* Foster toggle — its own row (source `.filterRij.tweede`). */}
        <div className="status-overview__foster">
          {/* The same switch the other overviews and acc use, not a bare checkbox. */}
          <ToggleField
            checked={includeFoster}
            onChange={(e) => setIncludeFoster(e.target.checked)}
            label={(fosterCount === 1 ? k("foster.showOne") : k("foster.show")).replace("%d", String(fosterCount))}
          />
          <small className="status-overview__foster-state">
            {includeFoster ? k("foster.active") : k("foster.inactive")}
          </small>
        </div>
        </>
        )}
      </section>


      {/* Results sub-bar */}
      <div className="status-overview__resultsbar">
        <span className="status-overview__results-count">
          <PawIcon width={16} height={16} /> {filtered.length}{" "}
          {filtered.length === 1 ? k("results.foundOne") : k("results.found")}
        </span>
        <div className="status-overview__resultsbar-right">
          <VeldenChooser
            velden={velden}
            onChange={setVelden}
            fieldLabel={(key) => k(`field.${key}`)}
            openLabel={k("veldkiezer.open")}
            titleLabel={k("veldkiezer.title")}
            cancelLabel={k("veldkiezer.cancel")}
            saveLabel={k("veldkiezer.save")}
          />
          <div className="status-overview__weergave" role="group" aria-label={k("veldkiezer.open")}>
            {(["grid", "compact", "lijst"] as const).map((w) => (
              <button
                key={w}
                type="button"
                className={weergave === w ? "is-active" : ""}
                aria-pressed={weergave === w}
                aria-label={k(`weergave.${w}`)}
                title={k(`weergave.${w}`)}
                onClick={() => setWeergave(w)}
              >
                <span aria-hidden="true">{w === "grid" ? "▦" : w === "compact" ? "▥" : "☰"}</span>
              </button>
            ))}
        </div>
        <label className="status-overview__field status-overview__sort">
          <span className="status-overview__field-label">{k("sort.label")}</span>
          <select className="status-overview__select" value={sortBy} aria-label={k("sort.label")} onChange={(e) => setSortBy(e.target.value as MedSort)}>
            {MED_SORTS.map((s) => (
              <option key={s} value={s}>{k(`sort.${s}`)}</option>
            ))}
          </select>
        </label>
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{k("loadFailed")}</p>
      ) : visible.length === 0 ? (
        <p className="fawkes-muted">{k("empty")}</p>
      ) : weergave === "lijst" ? (
        <div className="status-overview__list">
          <IndeelbareTabel
            tabelKey="medicijn-overzicht"
            columns={[
              {
                key: "status", header: k("table.status"),
                cell: (c) => {
                  const tone = STATUS_TONE[c.status] ?? "neutral";
                  return (
                    <span className={`status-list__pill status-list__pill--${tone}`}>
                      {k(`status.${c.status}`)}
                    </span>
                  );
                },
              },
              {
                key: "dier", header: k("table.dier"),
                cell: (c) => {
                  const foto = photoOf(c);
                  return (
                    <span className="status-list__dier">
                      {foto ? (
                        <img className="status-list__foto" src={foto} alt={c.naam ?? ""} />
                      ) : (
                        <span className="status-list__foto status-list__foto--empty" aria-hidden="true">
                          <PawIcon width={16} height={16} strokeWidth={1.5} />
                        </span>
                      )}
                      <span className="status-list__dier-text">
                        <b>
                          {c.naam || "—"}
                          {c.pleeggezin
                            ? <span className="status-list__foster-tag">🏠 {k("card.foster")}</span>
                            : null}
                        </b>
                        <span>{c.ras || c.diersoort || "Onbekend ras"}</span>
                      </span>
                    </span>
                  );
                },
              },
              // Welke velden er staan bepaalt de gebruiker met de veldenkiezer; dat bleef zo.
              ...FIELDS.filter((f) => velden[f.key]).map((f) => ({
                key: f.key,
                header: k(`field.${f.key}`),
                cell: (c: MedicijnCard) => {
                  const tone = STATUS_TONE[c.status] ?? "neutral";
                  return f.pill ? (
                    <span className={`status-card__verval status-card__verval--${tone}`}>
                      {c.volgendeToediening || "-"}
                    </span>
                  ) : (f.get(c) || "—");
                },
              })),
              {
                key: "acties", header: "", vast: true,
                cell: (c) => (
                  <span className="status-list__acties">
                    <button type="button" className="status-card__details"
                      onClick={() => onOpenAnimal(String(c.dierId))}>
                      {k("card.details")}
                    </button>
                    <button
                      type="button"
                      className="status-card__iconbtn"
                      title={k("card.bewerken")}
                      aria-label={k("card.bewerken")}
                      // Toediening registreren gebeurt op het dierscherm zolang die tegel niet gemigreerd is.
                      onClick={() => onOpenAnimal(String(c.dierId))}
                    >
                      <PencilIcon width={16} height={16} />
                    </button>
                  </span>
                ),
              },
            ]}
            rows={visible}
            getRowKey={(c) => String(c.dierId)}
            rowClassName={(c) => (c.pleeggezin
              ? "status-list__row status-list__row--foster"
              : "status-list__row")}
            empty=""
          />
        </div>
      ) : (
        <div className={`status-overview__grid${weergave === "compact" ? " status-overview__grid--compact" : ""}`}>
          {visible.map((c) => (
            <DierMedicijnCard
              key={c.dierId}
              card={c}
              photoUrl={photoOf(c)}
              velden={velden}
              tone={STATUS_TONE[c.status] ?? "neutral"}
              statusLabel={k(`status.${c.status}`)}
              volgendeText={c.volgendeToediening || "-"}
              metaLabel={(key) => k(`field.${key}`)}
              chipLabel={k("card.chipLabel")}
              bewerkenTitle={k("card.bewerken")}
              fosterLabel={k("card.foster")}
              onDetails={() => onOpenAnimal(String(c.dierId))}
              // Bewerken (the Jmix "Toediening registreren" action): the administration dialog + the
              // Medicijnen tile are not migrated yet, so this opens the animal detail (the registration
              // happens there once that tile lands). Mirrors the entingen overview's deep-link affordance.
              onBewerken={() => onOpenAnimal(String(c.dierId))}
            />
          ))}
        </div>
      )}

      {/* Pagination (source `voet` footer): range text + numbered pager (1 · … · current±2 · … · last · ›). */}
      <footer className="status-overview__pager">
        <span className="status-overview__pager-range">
          {filtered.length === 0
            ? k("pager.empty")
            : k("pager.range")
                .replace("%a", String(start + 1))
                .replace("%b", String(Math.min(start + PER_PAGE, filtered.length)))
                .replace("%n", String(filtered.length))}
        </span>
        {totalPages > 1 && (
          <div className="status-overview__pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - current) <= 2)
              .map((p, idx, arr) => (
                <Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="status-overview__pager-dots">…</span>
                  )}
                  <button
                    type="button"
                    className={`status-overview__page${p === current ? " status-overview__page--active" : ""}`}
                    aria-current={p === current ? "page" : undefined}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </Fragment>
              ))}
            <button
              type="button"
              className="status-overview__page status-overview__page--next"
              disabled={current >= totalPages}
              onClick={() => setPage(current + 1)}
            >
              ›
            </button>
          </div>
        )}
      </footer>
    </div>
  );
}

/**
 * Een capsule — het icoon dat acc voor dit overzicht gebruikt.
 *
 * <p>Zelf getekend omdat @fawkes/ui er geen heeft, net als de spuit en de worm in StatusOverview. Een
 * druppel stond er eerst; die betekent op elk ander scherm vloeistof of water en niet medicatie.
 */
function PillIcon({ width = 20, height = 20 }: { width?: number; height?: number }) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="8" width="19" height="8" rx="4" transform="rotate(-45 12 12)" />
      <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
    </svg>
  );
}

function KpiTile({
  tone,
  value,
  label,
  sub,
  active,
  onClick,
  icon,
}: {
  tone: string;
  value: number;
  label: string;
  sub?: string;
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`status-kpi status-kpi--${tone}${active ? " status-kpi--active" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="status-kpi__icon" aria-hidden="true">{icon}</span>
      <span className="status-kpi__body">
        <span className="status-kpi__value">{value}</span>
        <span className="status-kpi__label">{label}</span>
        {sub ? <span className="status-kpi__sub">{sub}</span> : null}
      </span>
    </button>
  );
}

function DierMedicijnCard({
  card,
  photoUrl,
  velden,
  tone,
  statusLabel,
  volgendeText,
  metaLabel,
  chipLabel,
  bewerkenTitle,
  fosterLabel,
  onDetails,
  onBewerken,
}: {
  card: MedicijnCard;
  photoUrl?: string | null;
  velden: VeldenState;
  tone: string;
  statusLabel: string;
  volgendeText: string;
  metaLabel: (key: string) => string;
  chipLabel: string;
  bewerkenTitle: string;
  fosterLabel: string;
  onDetails: () => void;
  onBewerken: () => void;
}) {
  const rows = FIELDS.filter((f) => velden[f.key]).map((r) => ({ key: r.key, value: r.get(card), pill: r.pill }));
  // Source parity: the whole card is the click target that opens the animal (no separate Details button).
  return (
    <div
      className={card.pleeggezin ? "status-card status-card--foster" : "status-card"}
      role="button"
      tabIndex={0}
      title="Open dier"
      onClick={onDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onDetails();
        }
      }}
    >
      <div className="status-card__media">
        {card.verblijfNummer ? <span className="status-card__id">{card.verblijfNummer}</span> : null}
        {card.pleeggezin ? <span className="status-card__foster-badge">🏠 {fosterLabel}</span> : null}
        {photoUrl ? (
          <img className="status-card__image" src={photoUrl} alt={card.naam ?? ""} />
        ) : (
          // Source `.fotoPlaceholderImg`: the camera-with-paw illustration, no caption (matches acc).
          <img
            className="status-card__placeholder-img"
            src="/themes/backoffice/images/foto-niet-beschikbaar.png"
            alt=""
            aria-hidden="true"
          />
        )}
        <span className={`status-card__status status-card__status--${tone}`}>{statusLabel}</span>
      </div>

      <div className="status-card__body">
        <h3 className="status-card__name">{card.naam || "—"}</h3>
        <div className="status-card__breed">{card.ras || card.diersoort || "Onbekend ras"}</div>

        <div className="status-card__meta">
          {rows.map((r) => (
            <div className="status-card__meta-row" key={r.key}>
              <span className="status-card__meta-icon" aria-hidden="true">{FIELD_ICON[r.key]}</span>
              <span className="status-card__meta-label">{metaLabel(r.key)}:</span>
              {r.pill ? (
                <span className={`status-card__meta-value status-card__verval status-card__verval--${tone}`}>
                  {r.key === "volgendeToediening" ? volgendeText : r.value || "—"}
                </span>
              ) : (
                <span className="status-card__meta-value">{r.value || "—"}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="status-card__footer">
        <div className="status-card__chip">
          <span className="status-card__chip-label">
            <MicrochipIcon width={14} height={14} /> {chipLabel}
          </span>
          {card.chipnummer ? (
            <a
              className="status-card__chip-value status-card__chip-link"
              href={`https://www.petbase.eu/chipnummer-zoeken/?chip_number=${encodeURIComponent(card.chipnummer)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {card.chipnummer}
            </a>
          ) : (
            <span className="status-card__chip-value">—</span>
          )}
        </div>
        <div className="status-card__actions">
          {/* Source parity: only the "Toediening registreren" action — no invented ⚙ or Details button. */}
          <button
            type="button"
            className="status-card__iconbtn"
            title={bewerkenTitle}
            aria-label={bewerkenTitle}
            onClick={(e) => {
              e.stopPropagation();
              onBewerken();
            }}
          >
            <PencilIcon width={16} height={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** The list-view row (source `DierRij`): status pill + animal cell + the chosen field columns + actions. */
